/**
 * What the tab spec knows about the project's questions. Built by
 * `model/catalogue.ts` from the parsed questionnaire, the codebook and the
 * routing provider. Nothing outside the catalogue builds a codebook path.
 */
export type QuestionType =
  | "select-one"
  | "select-many"
  | "range"
  | "number"
  | "text"
  | "grid-one"
  | "grid-many"
  | "grid-range"
  | "grid-number"

export type Coverage = "full" | "partial" | "unsupported" | "failed"

export interface CatalogueOption {
  label: string
  code?: number
  /** "Don't know", "Prefer not to say": shown as a row, left out of nets and means. */
  exception: boolean
}

/**
 * One tabulated unit of a question: the question itself, one grid row, one
 * loop iteration, or one grid row of one iteration.
 */
export interface CatalogueInstance {
  /** Stable within the question. "" for a plain question. */
  id: string
  /**
   * The parts the id is made of, outer first: each loop iteration, the grid
   * row, the second dimension of the grid. [] for a plain question. One per
   * name of `CatalogueQuestion.dimensions`.
   */
  parts: string[]
  /** Grid row and/or iteration, for titles. "" for a plain question. */
  label: string
  /** Codebook path of the container that holds the option leaves, or of the numeric or text field. */
  path: string
  /** Option label to the codebook path of its BOOL leaf. Empty for number and text. */
  optionPaths: Record<string, string>
  /** The routing condition as a CALC expression. null = everyone is asked, or unknown. */
  asked: string | null
  /** null = no routing information was available. */
  coverage: Coverage | null
  /** Answers from people who were not asked. */
  offPath: number
  /** People who were asked and gave no answer. */
  missingOnPath: number
  /** false before data import, or when the loaded data has no field for it. */
  inData: boolean
}

export interface CatalogueQuestion {
  label: string
  text: string
  type: QuestionType
  hidden: boolean
  /** The options are an ordered scale (range question or range list). */
  ordinal: boolean
  options: CatalogueOption[]
  instances: CatalogueInstance[]
  /**
   * A name for each part of an instance id, outer first: the label of a
   * loop's driver question for each loop, "Rows" for the grid rows,
   * "Columns" for a grid's second dimension. [] for a plain question.
   */
  dimensions: string[]
  /**
   * The place in `dimensions` of the grid rows, as the catalogue built them:
   * the names do not say it (a loop's driver may be labelled "Rows").
   * Absent when the question has no grid rows: a plain question, or one
   * asked in a loop only. The dimensions after it are the grid's own.
   */
  rowsDimension?: number
  /** "data": a variable of the prepared data that is not in the questionnaire. Absent = a questionnaire question. */
  source?: "data"
  /**
   * For a table of yes or no fields from the data: what the engine's codebook
   * says of them (`question_type` of its TABLE, the library's own
   * classification). "single": at most one is marked; "multi": several may
   * be. Absent when the codebook does not say (an older engine).
   */
  answers?: "single" | "multi"
  /**
   * A grid of the data whose rows are sibling containers: the containers
   * directly under `container` (a codebook path) that are no variable of
   * their own and hold the field `field` in the same shape, one row each
   * ("Overall Brand NPS//Bank 1//score", "Overall Brand NPS//Bank 2//score",
   * ...). `leftOut`: the containers there that do not (no such field, or
   * another shape). Such a grid is added deliberately, never in bulk: the
   * untabled-data finding (`model/reconcile.ts`) leaves it out of its count
   * and list, and so do the lists that add every variable at once ("Select
   * all listed" of "Build the tables from the data"). Its label ("Overall Brand
   * NPS: score") is a name only and never a codebook path: every path of the
   * variable is one of its instances'. Absent for every other variable.
   */
  siblings?: { container: string; field: string; leftOut: number }
}

/**
 * What the values check (`runtime/valueCheck.ts`) found of one number field
 * of the prepared data: its lowest and highest value, and whether the
 * codebook calls it a whole number (INT); "empty" when nobody has a value;
 * "failed" when the check could not read it.
 */
export type ValueRange = { min: number; max: number; whole: boolean } | "empty" | "failed"

/** Where a table or banner group finds its question: undefined = the questionnaire. */
export type QuestionSource = "data" | undefined

export interface Catalogue {
  questions: CatalogueQuestion[]
  /**
   * Variables of the loaded codebook that are not in the questionnaire, each
   * with `source: "data"`; empty before data import. Kept apart from
   * `questions`, so nothing that walks the questionnaire ever meets them.
   */
  dataVariables?: CatalogueQuestion[]
  /** Every path in the loaded codebook; null before data import. */
  knownPaths: Set<string> | null
  /** false when the routing provider returned nothing. */
  hasRouting: boolean
  /**
   * true while the routing is being read (the screen sets it). With
   * `hasRouting` false it means "not in yet", not "not available"; either
   * way no tab book starts until it is in.
   */
  routingLoading?: boolean
  /** The project has no questionnaire: every variable comes from the data, there is no routing, no "Who was asked". */
  dataOnly?: true
  /**
   * What the values check of the session found, by the codebook path of each
   * number field that a table showing each value names (the store keeps it
   * over a new catalogue of the same session). A field not in it is not
   * checked yet: such a table waits, and no tab book starts.
   */
  valueRanges?: Record<string, ValueRange>
}

/** What the server's base check says about one question. Filled by `api/routingProvider.ts`. */
export interface RoutingQuestion {
  /** The routing condition as a CALC expression; null when there is none. Trust it only with `coverage: "full"`. */
  asked: string | null
  coverage: Coverage
  offPath: number
  missingOnPath: number
  /** The question sits in a loop: the one condition the server gives is for the first iteration only. */
  looped: boolean
}

export interface RoutingInfo {
  /** The flow the server walked, as it reported it. */
  variant: string
  dataHash: string
  /** Keyed by question label. */
  questions: Record<string, RoutingQuestion>
}

/** The list of a catalogue without `dataVariables`: one array, so one index. */
const NONE: CatalogueQuestion[] = []

/**
 * Each list's questions by label, the first of two with the same label, built
 * the first time the list is searched. A catalogue's arrays are never changed
 * in place (a new catalogue brings new arrays), so an index never goes stale.
 */
const indexes = new WeakMap<CatalogueQuestion[], Map<string, CatalogueQuestion>>()

const indexOf = (list: CatalogueQuestion[]): Map<string, CatalogueQuestion> => {
  const known = indexes.get(list)
  if (known) return known
  const index = new Map<string, CatalogueQuestion>()
  list.forEach((q) => {
    if (!index.has(q.label)) index.set(q.label, q)
  })
  indexes.set(list, index)
  return index
}

/** `source` "data" looks in `dataVariables`, otherwise in `questions`. */
export const findQuestion = (
  catalogue: Catalogue,
  label: string,
  source?: QuestionSource
): CatalogueQuestion | null =>
  indexOf(source === "data" ? (catalogue.dataVariables ?? NONE) : catalogue.questions).get(
    label
  ) ?? null

export const findInstance = (
  question: CatalogueQuestion,
  id: string | null
): CatalogueInstance | null =>
  question.instances.find((i) => i.id === (id ?? "")) ?? null
